import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { CheckCircle2, Calendar, Phone, Mail, Sparkles, Clock, Circle, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import axios from "axios";
import { BrandMark } from "@/lib/branding";
import { parseLocalIso } from "@/lib/dt";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * EstStat — indicador tipográfico usado no card "Serviço Proposto" da
 * estimativa pública. Renderiza label em uppercase com tracking largo e
 * valor em Outfit bold; suporta `highlight` para destacar o valor em
 * laranja Caring (usado no "Valor semanal" / "Extra aplicado").
 */
function EstStat({ label, v, highlight }) {
  return (
    <div>
      <div
        className="font-bold"
        style={{
          color: "rgba(255,255,255,0.65)",
          fontSize: 12,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
        }}
      >
        {label}
      </div>
      <div
        className="mt-3"
        style={{
          fontFamily: '"Outfit", "Manrope", system-ui, sans-serif',
          color: highlight ? "#ffbd70" : "#ffffff",
          fontSize: "clamp(22px, 3vw, 27px)",
          fontWeight: 700,
          letterSpacing: "-0.01em",
          lineHeight: 1.1,
        }}
      >
        {v}
      </div>
    </div>
  );
}

export default function PublicEstimate() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const isPreview = searchParams.get("preview") === "1";
  const [d, setD] = useState(null);
  const [error, setError] = useState(null);
  const [copy, setCopy] = useState({});
  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/public/estimate/${token}`).then((r)=>setD(r.data)).catch(()=>setError("Estimativa não encontrada"));
    axios.get(`${BACKEND_URL}/api/public-copy`).then((r)=>setCopy(r.data || {})).catch(()=>{});
  }, [token]);

  // Live preview mode — listen for cfg pushed by the parent window
  // (PublicCopySettings.jsx iframe) so the admin sees unsaved edits in
  // real time without needing to save the singleton first.
  useEffect(() => {
    if (!isPreview) return;
    const handler = (e) => {
      const msg = e && e.data;
      if (!msg || msg.type !== "caring-preview-cfg" || !msg.cfg) return;
      setCopy(msg.cfg);
    };
    window.addEventListener("message", handler);
    // Signal the parent that we're ready to receive cfg updates.
    try { window.parent?.postMessage({ type: "caring-preview-ready" }, "*"); } catch (_) {}
    return () => window.removeEventListener("message", handler);
  }, [isPreview]);

  // Compute the theme (CSS variables) + section order once we have the config
  const themeVars = {
    "--pub-bg":           copy.color_bg           || "#0F172A",
    "--pub-surface":      copy.color_surface      || "#0F172A",
    "--pub-accent":       copy.color_accent       || "#3B82F6",
    "--pub-accent-soft":  copy.color_accent_soft  || "#F0D6AD",
    "--pub-text":         copy.color_text         || "#FFFFFF",
    "--pub-text-muted":   copy.color_text_muted   || "#64748B",
    "--pub-border":       copy.color_border       || "#FFFFFF1A",
  };
  const sectionsOrder = copy.sections_order || [
    "hero", "primary_option", "service_window", "other_options",
    "rt_block", "contact_footer",
  ];
  const orderOf = (id) => {
    const i = sectionsOrder.indexOf(id);
    return i === -1 ? 999 : i;
  };

  if (error) return (
    <div className="min-h-[70vh] flex items-center justify-center px-6">
      <div className="max-w-lg text-center">
        <div className="shift-chip shift-chip-danger mb-6 mx-auto">Estimativa indisponível</div>
        <h1 className="font-serif text-4xl md:text-5xl font-bold tracking-tight text-foreground mb-4">{error}</h1>
        <p className="text-muted-foreground text-lg leading-relaxed">
          Este link pode ter expirado ou já não estar activo. Contacte o consultor Caring com quem falou para receber uma nova ligação.
        </p>
      </div>
    </div>
  );
  if (!d) return (
    <div className="min-h-[70vh] flex items-center justify-center px-6">
      <div className="text-center">
        <div className="shift-chip mb-6 mx-auto">A preparar</div>
        <h1 className="font-serif text-3xl md:text-4xl font-bold tracking-tight text-foreground mb-2">A carregar a estimativa…</h1>
        <p className="text-muted-foreground">Um instante enquanto obtemos os detalhes.</p>
      </div>
    </div>
  );

  const c = d.qualification?.contact || {};
  const est = d.estimate || {};
  const options = est.options || [];
  const primary = options.find((o) => o.is_primary) || options[0] || null;
  const others = options.filter((o) => o !== primary);
  const fmt = (n) => (Number(n) || 0).toLocaleString("pt-PT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  // 25/02/2026 — Formatadores dedicados ao redesign do card "Serviço Proposto":
  // fmtMoney usa um formatador manual para garantir o separador de milhares "."
  // (o Intl pt-PT do Chromium omite-o abaixo de 10 000). fmtInt trunca casas em
  // dias; fmtHours acrescenta "h" com espaço. Sem alteração de cálculo.
  const fmtMoney = (n) => {
    const num = Number(n) || 0;
    const [intPart, decPart] = num.toFixed(2).split(".");
    const intWithDots = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    return `${intWithDots},${decPart} €`;
  };
  const fmtInt = (n) => `${Math.round(Number(n) || 0)}`;
  const fmtHours = (n) => {
    const v = Number(n) || 0;
    const s = Number.isInteger(v)
      ? String(v)
      : v.toLocaleString("pt-PT", { minimumFractionDigits: 1, maximumFractionDigits: 2 });
    return `${s} h`;
  };

  return (
    <div className="min-h-screen text-white" style={{ ...themeVars, background: "var(--pub-bg)", color: "var(--pub-text)" }} data-testid="public-estimate-root">
      <header className="sticky top-0 z-10 backdrop-blur-xl border-b" style={{ background: "color-mix(in oklab, var(--pub-bg) 80%, transparent)", borderColor: "var(--pub-border)" }}>
        <div className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between">
          <BrandMark size={36} />
          <div className="text-xs hidden sm:block" style={{ color: "var(--pub-text-muted)" }}>{copy.est_recipient_label || "Documento personalizado para"} {c.name}</div>
        </div>
      </header>

      {/* Middle flex column — sections are ordered via CSS `order` so the
          admin can rearrange them in /settings/public-copy without touching
          any code. */}
      <div className="flex flex-col">

      <section className="max-w-5xl mx-auto px-6 pt-16 lg:pt-24 pb-12" style={{ order: orderOf("hero") }} data-testid="section-hero">
        <div className="text-[10px] uppercase tracking-[0.3em] text-primary font-semibold mb-4">
          {copy.estimate_hero_kicker || "Estimativa personalizada"}
        </div>
        <h1 className="font-display text-5xl lg:text-6xl leading-[1.05] tracking-tight mb-6">
          {(copy.estimate_hero_title || "Preparámos uma primeira estimativa,\ncom base no que nos partilhou.")
            .split("\n").map((line, i, arr) => (
              <span key={i} className={i === arr.length - 1 && arr.length > 1 ? "text-white/50" : ""}>
                {line}{i < arr.length - 1 && <br/>}
              </span>
            ))}
        </h1>
        {est.summary && <p className="text-white/60 text-lg max-w-2xl leading-relaxed">{est.summary}</p>}
      </section>

      {/* PRIMARY OPTION — highlighted */}
      {primary && (
        <section className="max-w-5xl mx-auto px-6 py-6">
          {/* Data pretendida de início do serviço — sempre visível quando
              houver informação, quer venha da estimativa (definida pelo
              Gestor) quer venha do guião (indicada pelo cliente). Para
              serviços pontuais mostra também data de fim + total de
              dias; para continuados mostra só o início. */}
          {(() => {
            const qualStart = ((d.qualification || {}).service || {}).start_date;
            const startDate = est.service_start_date || qualStart;
            const endDate = est.service_end_date;
            if (!startDate && !endDate) return null;
            const isPontual = est.continuity_type === "pontual" || primary.mode === "pontual";
            const fromClient = !est.service_start_date && qualStart;
            const fmtDate = (iso) => {
              if (!iso) return "—";
              try { return new Date(iso).toLocaleDateString("pt-PT", { day: "2-digit", month: "long", year: "numeric" }); }
              catch { return iso; }
            };
            let dayCount = null;
            if (isPontual && startDate && endDate) {
              const s = new Date(startDate), e = new Date(endDate);
              if (!isNaN(s) && !isNaN(e) && e >= s) dayCount = Math.round((e - s) / 86400000) + 1;
            }
            return (
              <div
                className="mb-6 rounded-[24px] text-white px-6 py-6 sm:px-10 sm:py-8"
                style={{
                  background: "linear-gradient(135deg, rgba(49, 69, 143, 0.55) 0%, rgba(38, 56, 117, 0.55) 100%)",
                  border: "1px solid rgba(255, 255, 255, 0.08)",
                  backdropFilter: "blur(6px)",
                }}
                data-testid="public-service-window"
              >
                <div
                  className="font-bold uppercase mb-6 text-[12px] sm:text-[13px]"
                  style={{ color: "#ffb15c", letterSpacing: "0.22em" }}
                >
                  {isPontual
                    ? (copy.est_service_window_kicker || "Período do serviço")
                    : (copy.est_service_start_kicker || "Início do serviço")}
                  {fromClient && (
                    <span className="ml-2 font-medium normal-case" style={{ color: "rgba(255,255,255,0.55)", letterSpacing: "normal" }}>· conforme indicou</span>
                  )}
                </div>
                <div className={`grid grid-cols-1 ${isPontual ? "md:grid-cols-3" : "md:grid-cols-2"} gap-6`}>
                  <div>
                    <div
                      className="font-bold"
                      style={{ color: "rgba(255,255,255,0.65)", fontSize: 12, letterSpacing: "0.18em", textTransform: "uppercase" }}
                    >
                      {copy.est_service_window_start || "Início"}
                    </div>
                    <div
                      className="mt-2"
                      style={{
                        fontFamily: '"Outfit", "Manrope", system-ui, sans-serif',
                        color: "#ffffff",
                        fontSize: "clamp(18px, 2.2vw, 22px)",
                        fontWeight: 600,
                        letterSpacing: "-0.01em",
                      }}
                      data-testid="public-service-start"
                    >
                      {fmtDate(startDate)}
                    </div>
                  </div>
                  {isPontual && (
                    <>
                      <div>
                        <div
                          className="font-bold"
                          style={{ color: "rgba(255,255,255,0.65)", fontSize: 12, letterSpacing: "0.18em", textTransform: "uppercase" }}
                        >
                          {copy.est_service_window_end || "Fim"}
                        </div>
                        <div
                          className="mt-2"
                          style={{
                            fontFamily: '"Outfit", "Manrope", system-ui, sans-serif',
                            color: "#ffffff",
                            fontSize: "clamp(18px, 2.2vw, 22px)",
                            fontWeight: 600,
                            letterSpacing: "-0.01em",
                          }}
                        >
                          {fmtDate(endDate)}
                        </div>
                      </div>
                      <div>
                        <div
                          className="font-bold"
                          style={{ color: "rgba(255,255,255,0.65)", fontSize: 12, letterSpacing: "0.18em", textTransform: "uppercase" }}
                        >
                          {copy.est_service_window_total_days || "Total de dias"}
                        </div>
                        <div
                          className="mt-2"
                          style={{
                            fontFamily: '"Outfit", "Manrope", system-ui, sans-serif',
                            color: "#ffffff",
                            fontSize: "clamp(18px, 2.2vw, 22px)",
                            fontWeight: 600,
                            letterSpacing: "-0.01em",
                          }}
                        >
                          {dayCount ? `${dayCount} dia(s)` : "—"}
                        </div>
                      </div>
                    </>
                  )}
                  {!isPontual && (
                    <div>
                      <div
                        className="font-bold"
                        style={{ color: "rgba(255,255,255,0.65)", fontSize: 12, letterSpacing: "0.18em", textTransform: "uppercase" }}
                      >
                        Modalidade
                      </div>
                      <div
                        className="mt-2"
                        style={{
                          fontFamily: '"Outfit", "Manrope", system-ui, sans-serif',
                          color: "#ffffff",
                          fontSize: "clamp(18px, 2.2vw, 22px)",
                          fontWeight: 600,
                          letterSpacing: "-0.01em",
                        }}
                      >
                        Serviço continuado
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}
          <div
            className="rounded-[28px] text-white px-6 py-10 sm:px-12 sm:py-14 lg:px-16 caring-glow"
            style={{
              background: "linear-gradient(135deg, #31458f 0%, #263875 100%)",
              boxShadow: "0 18px 45px rgba(14, 25, 55, 0.22)",
            }}
            data-testid="primary-option"
          >
            <div
              className="font-bold uppercase mb-8 sm:mb-10 text-[12px] sm:text-[14px]"
              style={{ color: "#ffb15c", letterSpacing: "0.22em" }}
              data-testid="primary-option-kicker"
            >
              {copy.est_primary_kicker || "Sugestão Caring"} · {primary.name}
            </div>
            {primary.mode === "pontual" ? (
              <>
                <div className="mb-8 sm:mb-10">
                  <div
                    className="text-white leading-[1.05]"
                    style={{
                      fontFamily: '"Outfit", "Manrope", system-ui, sans-serif',
                      fontSize: "clamp(34px, 5.5vw, 44px)",
                      fontWeight: 800,
                      letterSpacing: "-0.03em",
                    }}
                    data-testid="primary-option-price"
                  >
                    {fmtMoney(primary.total_value)}
                  </div>
                  <div
                    className="mt-2 font-medium"
                    style={{ color: "rgba(255,255,255,0.70)", fontSize: "clamp(14px, 1.5vw, 16px)" }}
                  >
                    {copy.est_primary_suffix_total || "Valor total"}
                  </div>
                </div>
                <div className="mb-8 sm:mb-10" style={{ height: 1, background: "rgba(255,255,255,0.22)" }} />
                <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
                  <EstStat label={copy.est_label_total_days || "Dias efetivos"} v={fmtInt(primary.total_days)} />
                  <EstStat label={copy.est_label_hours_per_day || "Horas/dia"} v={fmtHours(primary.hours_per_day)} />
                  <EstStat label={copy.est_label_hourly_price || "Preço/hora"} v={fmtMoney(primary.hourly_price)} />
                  <EstStat label={copy.est_label_extra_fee || "Extra aplicado"} v={fmtMoney(primary.extra_fee)} highlight />
                </div>
              </>
            ) : (
              <>
                <div className="mb-8 sm:mb-10">
                  <div
                    className="text-white leading-[1.05]"
                    style={{
                      fontFamily: '"Outfit", "Manrope", system-ui, sans-serif',
                      fontSize: "clamp(34px, 5.5vw, 44px)",
                      fontWeight: 800,
                      letterSpacing: "-0.03em",
                    }}
                    data-testid="primary-option-price"
                  >
                    {fmtMoney(primary.monthly_avg)}
                  </div>
                  <div
                    className="mt-2 font-medium"
                    style={{ color: "rgba(255,255,255,0.70)", fontSize: "clamp(14px, 1.5vw, 16px)" }}
                  >
                    {copy.est_primary_suffix_monthly || "Média mensal"}
                  </div>
                </div>
                <div className="mb-8 sm:mb-10" style={{ height: 1, background: "rgba(255,255,255,0.22)" }} />
                <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
                  <EstStat label={copy.est_label_days_per_week || "Dias/semana"} v={fmtInt(primary.days_per_week)} />
                  <EstStat label={copy.est_label_hours_per_day || "Horas/dia"} v={fmtHours(primary.hours_per_day)} />
                  <EstStat label={copy.est_label_hourly_price || "Preço/hora"} v={fmtMoney(primary.hourly_price)} />
                  <EstStat label={copy.est_label_weekly_value || "Valor semanal"} v={fmtMoney(primary.weekly_value)} highlight />
                </div>
              </>
            )}
          </div>
        </section>
      )}

      {/* ESTIMATE DISCLAIMER — reinforces that this is NOT a final offer.
          Rendered right below the primary card so the client always
          reads it before assuming a fixed monthly fee. */}
      {primary && (
        <section className="max-w-5xl mx-auto px-6 -mt-4 mb-8" data-testid="estimate-disclaimer">
          <div className="bg-amber-500/[0.08] border border-[hsl(var(--warning))]/40 rounded-2xl p-5 flex items-start gap-4 backdrop-blur-sm">
            <div className="w-9 h-9 rounded-full bg-amber-400/15 border border-[hsl(var(--warning))]/40 flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-4 h-4 text-amber-200" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-amber-200 font-heading font-semibold mb-1">
                {copy.est_disclaimer_kicker || "Estimativa"}
              </div>
              <div className="text-sm text-white/85 leading-relaxed">
                {copy.est_disclaimer_body ||
                  "Este valor é uma estimativa baseada nas informações fornecidas. A Proposta Final, com o valor definitivo, será apresentada na Reunião Técnica."}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* OTHER OPTIONS */}
      {others.length > 0 && (
        <section className="max-w-5xl mx-auto px-6 py-8" data-testid="other-options">
          <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 font-semibold mb-5">{copy.est_other_options_kicker || "Outras opções que pode considerar"}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {others.map((o) => {
              const isP = o.mode === "pontual";
              const oPrice = isP ? o.total_value : o.monthly_avg;
              const oPriceLabel = isP ? (copy.est_primary_suffix_total || "Valor total") : (copy.est_primary_suffix_monthly || "Média mensal");
              return (
                <div key={o.id} className="bg-white/10 border border-white/5 rounded-2xl p-6" data-testid={`other-option-${o.id}`}>
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div className="font-ui text-lg">{o.name}</div>
                    <div className="text-right">
                      <div
                        style={{
                          fontFamily: '"Outfit", "Manrope", system-ui, sans-serif',
                          fontSize: "clamp(20px, 2.4vw, 26px)",
                          fontWeight: 700,
                          letterSpacing: "-0.02em",
                          lineHeight: 1.1,
                          color: "#ffb15c",
                        }}
                      >
                        {fmtMoney(oPrice)}
                      </div>
                      <div className="text-[11px] text-white/50 mt-1">{oPriceLabel}</div>
                    </div>
                  </div>
                  <div className="text-xs text-white/50 flex flex-wrap gap-x-4 gap-y-1">
                    {isP ? (<>
                      <span>{fmtInt(o.total_days)} dias</span>
                      <span>{fmtHours(o.hours_per_day)}/dia</span>
                      <span>{fmtMoney(o.hourly_price)}/h</span>
                      {o.extra_fee > 0 && <span>+{fmtMoney(o.extra_fee)} extra</span>}
                    </>) : (<>
                      <span>{fmtInt(o.days_per_week)} dias/sem</span>
                      <span>{fmtHours(o.hours_per_day)}/dia</span>
                      <span>{fmtMoney(o.hourly_price)}/h</span>
                      <span>{fmtMoney(o.weekly_value)}/semana</span>
                    </>)}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* SERVICE LEVELS REFERENCE — tabela de recomendações */}
      {Array.isArray(d.service_levels) && d.service_levels.length > 0 && (() => {
        const pickedDaily = new Set(
          (est.options || []).map((o) => Number(o.hours_per_day || 0)).filter((h) => h > 0)
        );
        return (
          <section className="max-w-5xl mx-auto px-6 py-10" data-testid="service-levels-section">
            <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 font-semibold mb-3">
              Referência de Níveis de Serviço
            </div>
            <h2 className="font-ui text-2xl md:text-3xl mb-4">
              O que está incluído em cada nível?
            </h2>
            <p className="text-sm text-white/60 mb-6 max-w-3xl">
              Cada opção da sua estimativa corresponde a um nível de cuidado.
              A tabela abaixo mostra o que está tipicamente incluído — para que
              possa comparar e escolher com clareza. As linhas realçadas a laranja
              são as que aparecem na sua estimativa.
            </p>
            <div className="overflow-x-auto rounded-2xl border border-white/10">
              <table className="w-full text-sm text-left border-collapse" data-testid="service-levels-table">
                <thead>
                  <tr className="bg-white/10 border-b border-white/10">
                    <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-white/40 font-semibold">Nível</th>
                    <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-white/40 font-semibold whitespace-nowrap">Horas</th>
                    <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-white/40 font-semibold">O que está incluído</th>
                  </tr>
                </thead>
                <tbody>
                  {d.service_levels.map((lvl) => {
                    const picked = pickedDaily.has(Number(lvl.hours_per_day));
                    return (
                      <tr key={lvl.id}
                          data-picked={picked ? "1" : "0"}
                          className={`border-b border-white/5 align-top ${picked ? "bg-orange-500/10 border-l-4 border-l-orange-400" : "bg-white/5"}`}>
                        <td className="px-4 py-4 min-w-[160px]">
                          <div className="font-ui text-base leading-tight">{lvl.level}</div>
                          <div className="text-[10px] text-white/50 mt-1 uppercase tracking-widest">{lvl.tag}</div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="font-ui text-lg text-primary">{lvl.hours_per_day}h/dia</div>
                          <div className="text-[10px] text-white/50 mt-1 leading-tight">
                            Sem. dias úteis: <b className="text-white/70">{lvl.hours_weekdays}h</b><br />
                            Fim de semana: <b className="text-white/70">{lvl.hours_weekend}h</b>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-white/70 text-xs italic mb-2">{lvl.description}</div>
                          <ul className="space-y-1">
                            {(lvl.bullets || []).map((b, i) => (
                              <li key={i} className="text-xs text-white/70 flex gap-2">
                                <span className="text-orange-400 mt-0.5">•</span>
                                <span>{b}</span>
                              </li>
                            ))}
                          </ul>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="text-[11px] text-white/40 mt-3">
              Estes valores são orientativos — a Reunião Técnica confirmará o plano exacto para o seu caso.
            </div>
          </section>
        );
      })()}

      {/* QT ADJUSTMENT — before/after transparency */}
      {est.qt_adjusted && est.qt_snapshot_before && (() => {
        const snap = est.qt_snapshot_before;
        const beforePrimary = (snap.options || []).find((o) => o.is_primary) || (snap.options || [])[0];
        if (!beforePrimary || !primary) return null;
        const isP = primary.mode === "pontual";
        const beforeValue = isP ? beforePrimary.total_value : beforePrimary.monthly_avg;
        const afterValue = isP ? primary.total_value : primary.monthly_avg;
        const delta = afterValue - beforeValue;
        const deltaCls = delta > 0 ? "text-rose-300" : delta < 0 ? "text-emerald-300" : "text-white/60";
        return (
          <section className="max-w-5xl mx-auto px-6 py-10" data-testid="qt-adjusted-section">
            <div className="text-[10px] uppercase tracking-[0.25em] text-primary font-semibold mb-3">
              {copy.est_qt_adjusted_kicker || "Alteração após Reunião Técnica"}
              {est.qt_confirmed_by_name && <span className="text-white/40 ml-2">· por {est.qt_confirmed_by_name}</span>}
            </div>
            <div className="bg-white/10 border border-primary/20 rounded-2xl p-8 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-card/[0.02] border border-white/5 rounded-xl p-5">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-semibold mb-2">{copy.est_qt_before_label || "Estimativa inicial"}</div>
                  <div className="font-ui text-2xl text-white/70 line-through decoration-white/20">{fmt(beforeValue)} €<span className="text-white/40 text-sm no-underline">{isP ? ` ${copy.est_primary_suffix_total || "total"}` : ` ${copy.est_primary_suffix_monthly || "/mês"}`}</span></div>
                  <div className="text-[11px] text-white/40 mt-2">{beforePrimary.name}</div>
                </div>
                <div className="bg-primary/[0.06] border border-primary/25 rounded-xl p-5">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-primary font-semibold mb-2">{copy.est_qt_after_label || "Valor final"}</div>
                  <div className="font-ui text-2xl text-white">{fmt(afterValue)} €<span className="text-white/40 text-sm">{isP ? ` ${copy.est_primary_suffix_total || "total"}` : ` ${copy.est_primary_suffix_monthly || "/mês"}`}</span></div>
                  <div className={`text-[11px] mt-2 ${deltaCls}`}>{delta > 0 ? "+" : ""}{fmt(delta)} € {delta === 0 ? "(sem alteração de valor)" : ""}</div>
                </div>
              </div>
              {est.qt_adjustment_reason && (
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-semibold mb-2">{copy.est_qt_reason_label || "Motivo do ajuste"}</div>
                  <div className="text-white/75 text-sm leading-relaxed">{est.qt_adjustment_reason}</div>
                </div>
              )}
            </div>
          </section>
        );
      })()}

      {est.qt_confirmed_at && !est.qt_adjusted && (
        <section className="max-w-5xl mx-auto px-6 py-6" data-testid="qt-confirmed-section">
          <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-4 py-2 text-emerald-300 text-xs">
            <span>✓</span>
            <span>{copy.est_qt_confirmed_text || "Valores confirmados após Reunião Técnica"}{est.qt_confirmed_by_name ? ` por ${est.qt_confirmed_by_name}` : ""}.</span>
          </div>
        </section>
      )}

      {/* Additional notes (free text) */}
      {est.additional_notes && (
        <section className="max-w-5xl mx-auto px-6 py-6">
          <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 font-semibold mb-3">{copy.est_notes_kicker || "Notas do Caring"}</div>
          <div className="bg-foreground/60 border border-white/5 rounded-2xl p-6 text-white/70 text-sm leading-relaxed whitespace-pre-wrap" data-testid="additional-notes">
            {est.additional_notes}
          </div>
        </section>
      )}

      {/* Commercial terms */}
      {est.commercial_terms && (
        <section className="max-w-5xl mx-auto px-6 py-10">
          <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 font-semibold mb-3">
            {copy.est_terms_kicker || "Condições comerciais"} {est.continuity_type && <span className="text-primary ml-2">· {est.continuity_type === "pontual" ? (copy.est_terms_pontual || "Serviço Pontual") : (copy.est_terms_continuidade || "Serviço Continuado")}</span>}
          </div>
          <div className="bg-white/10 border border-white/5 rounded-2xl p-8 text-white/70 text-sm leading-relaxed whitespace-pre-wrap" data-testid="terms">
            {est.commercial_terms}
          </div>
        </section>
      )}

      <section className="max-w-5xl mx-auto px-6 py-16">
        <h3 className="font-display text-3xl mb-8">{copy.est_next_steps_title || "Próximos passos"}</h3>
        <div className="space-y-4">
          {(() => {
            // ---- RT state machine (kept inline; refactoring would need
            // retesting all variants) ----
            const scheduledIso = d.scheduled_qt_at || "";
            const rtScheduled = !!scheduledIso;

            // Status keys → icon + colour class. LABELS come from admin copy
            // so the page can be fully translated without touching code.
            const STATUS_META = {
              done:       { Icon: CheckCircle2, cls: "text-emerald-400 border-emerald-400/30 bg-emerald-400/10" },
              scheduled:  { Icon: Calendar,     cls: "text-primary border-primary/40 bg-primary/10" },
              awaiting:   { Icon: Clock,        cls: "text-amber-300 border-[hsl(var(--warning))]/40 bg-amber-300/10" },
              preparing:  { Icon: Sparkles,     cls: "text-primary border-primary/40 bg-primary/10" },
              pending:    { Icon: Circle,       cls: "text-white/40 border-white/20 bg-card/5" },
            };
            const label = (key) => (copy[`est_status_${key}`] || key);

            // Substitute {phone} / {email} placeholders in body text so
            // the admin can move the contact block around without code.
            const withContact = (body) => (body || "")
              .replaceAll("{phone}", copy.contact_phone || "800 450 191")
              .replaceAll("{email}", copy.contact_email || "sac.qi@caring.pt");

            // Pretty-format the RT date/time for display on the Step 2
            // card. ``scheduledIso`` is stored as 'YYYY-MM-DDTHH:MM[:SS]'
            // in local Portugal time (no timezone suffix — see server.py
            // ``_schedule_retag_emails``). Locale-aware weekday + day +
            // month + year + hh:mm.
            let rtWhen = null;
            if (rtScheduled) {
              const dt = parseLocalIso(scheduledIso);
              if (dt) {
                const dateStr = dt.toLocaleDateString("pt-PT", {
                  weekday: "long", day: "2-digit", month: "long", year: "numeric",
                });
                const timeStr = dt.toLocaleTimeString("pt-PT", {
                  hour: "2-digit", minute: "2-digit",
                });
                rtWhen = { dateStr, timeStr };
              }
            }

            const steps = [
              {
                title: copy.est_step1_title || "Estimativa",
                statusKey: "done",
                body: copy.est_step1_body || "",
                extra: null,
              },
              {
                title: copy.est_step2_title || "Reunião Técnica",
                statusKey: rtScheduled ? "scheduled" : "awaiting",
                body: withContact(rtScheduled
                  ? copy.est_step2_body_scheduled
                  : copy.est_step2_body_awaiting),
                // Show the RT date/time chip on Step 2 only when the RT
                // is scheduled — reinforces trust that the process is
                // organized (user request 04/08/2026).
                extra: rtWhen ? (
                  <div
                    className="mt-3 mb-3 inline-flex items-center gap-3 rounded-xl bg-primary/10 border border-primary/40 px-4 py-3"
                    data-testid="rt-scheduled-when"
                  >
                    <Calendar className="w-4 h-4 text-primary flex-shrink-0" />
                    <div className="flex flex-col leading-tight">
                      <span className="text-[9px] uppercase tracking-[0.2em] text-white/50 font-heading">
                        Data agendada
                      </span>
                      <span className="text-sm text-white font-ui first-letter:capitalize">
                        {rtWhen.dateStr}
                      </span>
                      <span className="text-[11px] text-white/60 font-ui tabular-nums">
                        às {rtWhen.timeStr}
                      </span>
                    </div>
                  </div>
                ) : null,
              },
              {
                title: copy.est_step3_title || "Proposta Final e Início do Serviço",
                statusKey: rtScheduled ? "preparing" : "pending",
                body: rtScheduled ? copy.est_step3_body_preparing : copy.est_step3_body_pending,
                extra: null,
              },
            ];

            return steps.map((s, i) => {
              const meta = STATUS_META[s.statusKey] || STATUS_META.pending;
              const Icon = meta.Icon;
              return (
                <div
                  key={i}
                  className="flex items-start gap-4 bg-white/10 border border-white/5 rounded-2xl p-6"
                  data-testid={`next-step-${i}`}
                >
                  <div className="w-8 h-8 rounded-full bg-primary/10 border border-primary/30 text-primary flex items-center justify-center text-sm font-ui flex-shrink-0">
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-3 mb-1">
                      <div className="font-display text-lg text-white">{s.title}</div>
                      <span
                        className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] px-2.5 py-1 rounded-full border font-ui ${meta.cls}`}
                        data-testid={`step-status-${i}`}
                      >
                        <Icon className="w-3 h-3" />
                        {label(s.statusKey)}
                      </span>
                    </div>
                    {s.extra}
                    <div className="text-white/70 text-sm leading-relaxed whitespace-pre-line">
                      {s.body}
                    </div>
                  </div>
                </div>
              );
            });
          })()}
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 py-16 lg:py-24">
        {(() => {
          const rtAccepted = d.technical_meeting_accepted === true;
          const scheduledIso = d.scheduled_qt_at || "";
          const rtCompleted =
            !!(d.estimate && d.estimate.qt_confirmed_at) ||
            ["qt_realizada", "proposta_enviada", "aceite"].includes(d.status);
          const managerName = d.assigned_manager_name || "";
          let prettyWhen = "";
          if (scheduledIso) {
            const dt = parseLocalIso(scheduledIso);
            if (dt) {
              prettyWhen = dt.toLocaleString("pt-PT", {
                weekday: "long", day: "2-digit", month: "long", year: "numeric",
                hour: "2-digit", minute: "2-digit",
              });
            }
          }
          // Small helper — turn textarea line breaks into JSX for public copy
          const withBr = (text) => (text || "").split("\n").map((l, i, a) => (
            <span key={i}>{l}{i < a.length - 1 && <br/>}</span>
          ));
          // ---- Variant Z · RT already realized (overrides all others) ----
          // Once the manager marked the RT as done (qt_confirmed_at) or the
          // lead moved past qt_realizada, the "aguardar contacto" copy is
          // stale — show a post-RT thank-you block instead.
          if (rtCompleted) {
            return (
              <div className="bg-white/10 border border-primary/25 rounded-3xl p-10 lg:p-14 text-center" data-testid="rt-block-completed">
                <CheckCircle2 className="w-8 h-8 text-primary mx-auto mb-6" />
                <h3 className="font-display text-3xl lg:text-4xl mb-4">{copy.rt_completed_title || "Reunião técnica realizada"}</h3>
                <p className="text-white/60 max-w-2xl mx-auto mb-4 leading-relaxed">
                  {withBr(copy.rt_completed_body)}
                </p>
                <div className="text-sm text-white/50">{withBr(copy.rt_completed_footer)}</div>
              </div>
            );
          }
          // ---- Variant A · RT scheduled (date set → source of truth) ----
          // ``scheduled_qt_at`` alone is enough: once a date exists the
          // block must say "agendada", regardless of whether the
          // ``technical_meeting_accepted`` flag was flipped by the
          // gestor. Falling back to Variant C (which invited the client
          // to be contacted) was misleading when a slot was already
          // combined.
          if (prettyWhen) {
            return (
              <div className="bg-white/10 border border-primary/25 rounded-3xl p-10 lg:p-14 text-center" data-testid="rt-block-scheduled">
                <Calendar className="w-8 h-8 text-primary mx-auto mb-6" />
                <h3 className="font-display text-3xl lg:text-4xl mb-3">{copy.rt_scheduled_title || "Reunião técnica agendada"}</h3>
                <div className="text-primary text-lg mb-6 font-display">{prettyWhen}{managerName ? ` · com ${managerName}` : ""}</div>
                <p className="text-white/60 max-w-2xl mx-auto leading-relaxed">
                  {withBr(copy.rt_scheduled_body)}
                </p>
              </div>
            );
          }
          // ---- Variant B · RT accepted but not yet scheduled ----
          if (rtAccepted) {
            return (
              <div className="bg-white/10 border border-primary/25 rounded-3xl p-10 lg:p-14 text-center" data-testid="rt-block-accepted">
                <CheckCircle2 className="w-8 h-8 text-primary mx-auto mb-6" />
                <h3 className="font-display text-3xl lg:text-4xl mb-4">{copy.rt_accepted_title || "Reunião técnica confirmada"}</h3>
                <p className="text-white/60 max-w-2xl mx-auto mb-4 leading-relaxed">
                  {withBr(copy.rt_accepted_body)}
                </p>
                <div className="text-sm text-white/50">{withBr(copy.rt_accepted_footer)}</div>
              </div>
            );
          }
          // ---- Variant C · Client is still deciding (default) ----
          return (
            <div className="bg-white/10 border border-white/5 rounded-3xl p-10 lg:p-14 text-center" data-testid="rt-block-invite">
              <Sparkles className="w-8 h-8 text-primary mx-auto mb-6" />
              <h3 className="font-display text-3xl lg:text-4xl mb-4">{copy.rt_invite_title || "Reunião técnica personalizada"}</h3>
              <p className="text-white/60 max-w-2xl mx-auto mb-8 leading-relaxed">
                {withBr(copy.rt_invite_body)}
              </p>
              <div className="text-sm text-white/50">{withBr(copy.rt_invite_footer)}</div>
              <div className="flex flex-wrap items-center justify-center gap-4 mt-8">
                <a
                  href={copy.contact_phone_href || "tel:800450191"}
                  className="inline-flex items-center gap-2 bg-primary hover:bg-primary/85 text-white font-semibold rounded-full px-6 py-3 text-sm"
                  aria-label={`Ligar para ${copy.contact_phone || "800 450 191"} — chamada grátis`}
                >
                  <Phone className="w-4 h-4" />
                  <span>{copy.contact_phone || "800 450 191"}</span>
                  <span className="text-[11px] text-white/70 font-normal">· chamada grátis</span>
                </a>
                <a href={`mailto:${copy.contact_email || "sac.qi@caring.pt"}`} className="inline-flex items-center gap-2 bg-card/5 hover:bg-card/10 border border-white/10 rounded-full px-6 py-3 text-sm">
                  <Mail className="w-4 h-4" /> {copy.contact_email || "sac.qi@caring.pt"}
                </a>
              </div>
            </div>
          );
        })()}
      </section>

      </div>

      <footer className="border-t border-white/5 py-10 text-center text-xs text-white/30">
        © Caring · {copy.footer_tagline || "Cuidamos com dignidade"}
      </footer>
    </div>
  );
}
